import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import OrderService from '../../../services/order.service';

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 2
  },
  section: {
    mb: 3
  },
  label: {
    fontWeight: 'bold',
    color: 'text.secondary',
    mr: 1
  }
};

export default function TransactionDetailsModal({ open, onClose, data, provider }) {
  const [loadingOrders, setLoadingOrders] = useState({});
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  if (!data) return null;

  const { transaction_details, orders } = data;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const formatAmount = (amount) => {
    return Number(amount).toLocaleString('fr-FR') + ' FCFA';
  };

  const handleAssociatePayment = async (orderId, orderNumber) => {
    try {
      setLoadingOrders(prev => ({ ...prev, [orderId]: true }));
      setError(null);
      
      const response = await OrderService.verifyPayment({
        order_id: orderId,
        id_transaction: data.transaction_details.id_transaction,
        payment_partner: provider.toUpperCase()
      });

      if (response?.statut === 'success') {
        setSnackbarOpen(true);
        onClose();
      } else {
        throw new Error(response?.data?.message || 'Échec de l\'association');
      }
    } catch (err) {
      console.error('Erreur lors de l\'association:', err);
      setError(err.message || 'Erreur lors de l\'association du paiement');
    } finally {
      setLoadingOrders(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={styles.header}>
          <Typography variant="h6">
            Détails de la Transaction {transaction_details.id_transaction}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={styles.section}>
          <Typography variant="subtitle1" gutterBottom>
            Informations de la Transaction
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2">
                  <span sx={styles.label}>Montant:</span>
                  {formatAmount(transaction_details.montant)}
                </Typography>
                <Typography variant="body2">
                  <span sx={styles.label}>Méthode:</span>
                  {transaction_details.payment_method}
                </Typography>
                <Typography variant="body2">
                  <span sx={styles.label}>Opérateur:</span>
                  {transaction_details.operator}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2">
                  <span sx={styles.label}>Date:</span>
                  {formatDate(transaction_details.date)}
                </Typography>
                <Typography variant="body2">
                  <span sx={styles.label}>Devise:</span>
                  {transaction_details.currency}
                </Typography>
                <Typography variant="body2">
                  <span sx={styles.label}>Statut:</span>
                  <Chip
                    size="small"
                    label={transaction_details.statut}
                    color={transaction_details.statut === 'success' ? 'success' : 'error'}
                  />
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={styles.section}>
          <Typography variant="subtitle1" gutterBottom>
            Client
          </Typography>
          <Typography variant="body2">
            {transaction_details.customer_details.firstname} {transaction_details.customer_details.lastname}
          </Typography>
          <Typography variant="body2">
            {transaction_details.customer_details.email}
          </Typography>
        </Box>
        {orders && orders.length > 0 && (
          <Box sx={styles.section}>
            <Typography variant="subtitle1" gutterBottom>
              Commandes associées
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Numéro</TableCell>
                  <TableCell>Montant</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <React.Fragment key={order.id}>
                    <TableRow>
                      <TableCell>{order.number}</TableCell>
                      <TableCell>{formatAmount(order.amount)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={order.order_status}
                          color={order.order_status === 'success' ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>{formatDate(order.createdAt)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={loadingOrders[order.id] ? <CircularProgress size={20} /> : <LinkIcon />}
                          onClick={() => handleAssociatePayment(order.id, order.number)}
                          disabled={loadingOrders[order.id]}
                        >
                          Associer
                        </Button>
                      </TableCell>
                    </TableRow>
                    {order.orderItems && order.orderItems.map((item) => (
                      <TableRow key={item.id} sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell colSpan={4} sx={{ pl: 4 }}>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Typography variant="body2">
                              {item.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Quantité: {item.quantity}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Prix: {formatAmount(item.price)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Type: {item.type}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Paiement associé avec succès !
        </Alert>
      </Snackbar>
    </Dialog>
  );
} 